import { simulate } from './engine.ts';
import type { GameState, SimulationResult } from './types.ts';

export type GameStateListener = (state: GameState, result: SimulationResult) => void;

export interface GameLoopOptions {
  intervalMs?: number;
  clock?: () => number;
}

export class GameLoop {
  private state: GameState;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly intervalMs: number;
  private readonly clock: () => number;
  private readonly listeners = new Set<GameStateListener>();

  constructor(initialState: GameState, options: GameLoopOptions = {}) {
    this.state = initialState;
    this.intervalMs = options.intervalMs ?? 1_000;
    this.clock = options.clock ?? Date.now;
  }

  snapshot(): GameState {
    return this.state;
  }

  replaceState(state: GameState): void {
    this.state = state;
  }

  subscribe(listener: GameStateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  tick(now = this.clock()): SimulationResult {
    const result = simulate(this.state, now, { mode: 'active' });
    this.state = result.state;
    if (result.simulatedSeconds > 0) {
      for (const listener of this.listeners) listener(this.state, result);
    }
    return result;
  }

  restoreOffline(now = this.clock()): SimulationResult {
    const result = simulate(this.state, now, { mode: 'offline' });
    this.state = result.state;
    if (result.simulatedSeconds > 0) {
      for (const listener of this.listeners) listener(this.state, result);
    }
    return result;
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), this.intervalMs);
  }

  stop(): void {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}

